// useGenerateProfile.ts
import { ref, Ref, watch, watchEffect } from "vue";
import { useToast } from "primevue/usetoast";
import { getDisplayName } from "../GeneralUtils";
import { i18n } from "../../main";
import { ProfileContractorAPI } from "../../api";
import { useI18n } from "vue-i18n";
import { Participant, ProductLanguages, Project } from "../../types/PersologOnlineTypes";

export default function useGenerateProfile(
    project: Ref<Project>,
    participant: Ref<Participant>,
    languages: Ref<ProductLanguages | null>,
) {
    const loading = ref(false);
    const toast = useToast();
    const { t } = useI18n();

    const isDisabled = ref(!(participant.value.status === "GENERATED" || participant.value.status === "NOTIFIED"));
    const languagesString: Ref<string[]> = ref(getLanguages());

    const items = ref(setItems());

    const addToastError = (participant: Participant, language: string) => {
        toast.add({
            severity: "error",
            summary: t("project-details.participants.regenerate.heading"),
            detail: t("project-details.participants.regenerate.error", {
                firstname: participant.firstName,
                lastname: participant.name,
                language: getDisplayName(i18n.global.locale.value, language),
            }),
            life: 8000,
        });
    };

    const showSuccessToast = (participant: Participant, language: string) => {
        toast.add({
            severity: "success",
            summary: t("project-details.participants.regenerate.heading"),
            detail: t("project-details.participants.regenerate.success", {
                firstname: participant.firstName,
                lastname: participant.name,
                language: getDisplayName(i18n.global.locale.value, language),
            }),
            life: 8000,
        });
    };

    let pollingInterval: NodeJS.Timeout | undefined;

    async function generateProfile(language: string) {
        try {
            loading.value = true;
            await ProfileContractorAPI.generateProfile(
                participant.value.selectedProduct,
                participant.value.id as number,
                participant.value.questionnaire as string,
                language,
            );
            showSuccessToast(participant.value, language);

            // Start polling every 5 seconds
            pollingInterval = setInterval(async () => {
                const newLanguages = await ProfileContractorAPI.fetchLanguages(
                    participant.value.selectedProduct,
                    participant.value.id as number,
                );
                if (newLanguages.includes(language)) {
                    // Stop polling
                    if (pollingInterval) {
                        clearInterval(pollingInterval);
                    }
                    toast.add({
                        severity: "success",
                        summary: t("project-details.participants.regenerate.headingGenerated"),
                        detail: t("project-details.participants.regenerate.generated", {
                            firstname: participant.value.firstName,
                            lastname: participant.value.name,
                            language: getDisplayName(i18n.global.locale.value, language),
                        }),
                        life: 6000,
                    });
                }
            }, 5000); // Poll every 5 seconds

            // Stop polling after 5 minutes
            setTimeout(() => {
                if (pollingInterval) {
                    clearInterval(pollingInterval);
                }
            }, 300000);
        } catch (error) {
            console.error("Error generating profile:", error);
            addToastError(participant.value, language);
        } finally {
            loading.value = false;
        }
    }

    function setItems() {
        return languagesString.value.map((lang: string) => ({
            label: getDisplayName(i18n.global.locale.value, lang),
            command: async () => {
                await generateProfile(lang);
            },
        }));
    }

    function getLanguages() {
        const product = participant.value.selectedProduct ? participant.value.selectedProduct : project.value.product;
        return languages.value ? languages.value[product] : [];
    }

    watch(
        () => participant.value.status,
        async (newStatus) => {
            if (newStatus === "GENERATED" || newStatus === "NOTIFIED") {
                languagesString.value = getLanguages();
                isDisabled.value = false;
            }
        },
    );

    watchEffect(async () => {
        languagesString.value = getLanguages();
        items.value = setItems();
    });

    return {
        loading,
        generateProfile,
        items,
        isDisabled,
    };
}
